import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { useSnackbar } from "notistack";
import { useSelector } from "react-redux";
import dayjs from "dayjs";
import { ReactComponent as InspectionVehicleImg } from "assets/inspection-vehicle.svg";
import Loader from "layout/Loader";
import AlertMessages from "common/Alert/AlertMessages";
import { themes } from "theme/themes";
import { brandingInfo } from "features/server/brandingSlice";
import { getBrandingAboutHref } from "Helpers/brandingFooter";
import { useGetInvoiceTemplateByIDService } from "subModule/src/services/invoice/useInvoices";
import {
  CustomSettings,
  getDefaultSectionOrderForTemplateType,
  withDesignVariantInSettings,
} from "subModule/src/services/invoice/types";
import { usePreFillLabels } from "components/InvoiceTemplate/hooks/usePreFillLabels";
import { useUserLanguages } from "components/InvoiceTemplate/hooks/useUserLanguages";
import { useDefaultLabels } from "components/InvoiceTemplate/hooks/useDefaultLabels";
import {
  getThermalPaperWidthMm,
  isThermalPrint,
} from "components/InvoiceTemplate/thermal/thermalInvoiceUtils";
import { CommonProfileService } from "subModule/src/core/services";
import { VehicleInspectionByIdResponse } from "subModule/src/services/vehicle/interface";
import { useGetVehicleInspectionByIDService } from "subModule/src/services/vehicle/useVehicle";

const defaultSettings: CustomSettings = {
  languageDisplay: "both",
  dateFormat: "DD-MM-YYYY",
  showLogo: true,
  showHeader: true,
  showVehicleInfo: true,
  showTable: true,
  showTerms: true,
  showNotes: true,
  logoPosition: "left",
  logoWidth: 150,
  logoHeight: 80,
  useDefaultLogoSize: true,
  showQR: false,
  showCompanySection: true,
  showCustomerSection: true,
  invoiceHeadingPrimary: "INSPECTION",
  invoiceHeadingSecondary: "",
  showInvoiceDirection: "column",
  companyFields: {
    name: true,
    phone: true,
    email: true,
    address: true,
    vat: false,
  },
  customerFields: {
    name: true,
    phone: true,
    email: false,
    address: false,
    vat: false,
  },
  vehicleFields: {
    color: true,
    fuelType: true,
    vin: true,
    mileage: true,
    make: true,
    model: true,
    year: true,
    plateNumber: true,
  },
  tableLayout: "combined",
  showPackages: false,
  showServices: false,
  showItems: false,
  descriptionDisplay: "all",
  tableColumns: {
    serialNo: true,
    description: true,
    quantity: true,
    unitPrice: true,
    discount: true,
    tax: true,
    amount: true,
  },
  consolidatedTableColumns: {
    serialNo: true,
    vehicleName: true,
    vehiclePlateNo: true,
    deliveryNoteNo: true,
    subtotal: true,
    discount: true,
    tax: true,
    total: true,
  },
  summaryItems: {
    subtotal: false,
    discount: false,
    discountedSubtotal: false,
    tax: false,
    total: false,
  },
  showPaymentInfo: false,
  printFormat: "normal",
  thermalPaperWidthPreset: "80",
  thermalCustomWidthMm: 80,
  showFooter: true,
  footerText: "This is system generated inspection.",
  footerItems: {
    address: true,
    email: true,
    phone: true,
    socialLinks: false,
    pageNumber: true,
  },
  // Inspection-specific toggles (kept permissive; template may override)
  showInspectionItems: true,
  showVehicleImage: true,
  showInspectionRemarks: true,
  showTechnicians: true,
  showCustomerSignature: true,
  sectionOrder: getDefaultSectionOrderForTemplateType(5),
};

const InspectionDownloadPreview = () => {
  const { t } = useTranslation();
  const params = useParams();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const { preFillLabels } = usePreFillLabels();
  const { secondaryLanguage } = useUserLanguages();
  const branding = useSelector(brandingInfo);
  const themeColors =
    branding?.attributes?.themeId === 100
      ? branding?.attributes?.customTheme
      : (themes as any)[branding?.attributes?.themeId || 3];

  const [settings, setSettings] = useState<CustomSettings>(defaultSettings);
  const [inspection, setInspection] = useState<VehicleInspectionByIdResponse>(
    {},
  );
  const [loading, setLoading] = useState<boolean>(true);
  const [downloading, setDownloading] = useState<boolean>(false);
  const [stationLogoBase64Src, setStationLogoBase64Src] = useState<string>("");

  const printRef = useRef<HTMLDivElement>(null);
  const { isRefetching, refetch: templateByIdRefetch } =
    useGetInvoiceTemplateByIDService(Number(params?.templateId));
  const { isRefetching: inspectionRefetching, refetch: inspectionByIdRefetch } =
    useGetVehicleInspectionByIDService(Number(params?.id));

  const extractTemplateSettings = (data: any): CustomSettings => {
    const rawAttributes = data?.attributes;
    const nested =
      rawAttributes?.attributes ||
      rawAttributes?.settings ||
      data?.settings ||
      null;

    if (nested && typeof nested === "object") {
      return nested as CustomSettings;
    }

    return (rawAttributes || {}) as CustomSettings;
  };

  const getTemplateById = useCallback(() => {
    return templateByIdRefetch().then((res: any) => {
      if (res?.data?.success) {
        const data = res?.data?.data || {};
        const templateSettings = withDesignVariantInSettings(
          extractTemplateSettings(data),
          data.designVariant,
        );
        const enriched = preFillLabels({
          ...defaultSettings,
          ...templateSettings,
        });
        setSettings(enriched);
      } else {
        enqueueSnackbar(
          <AlertMessages text={res?.data?.message || "Something went wrong"} />,
          { variant: "error" },
        );
      }
    });
  }, [enqueueSnackbar, preFillLabels, templateByIdRefetch]);

  const getInspectionById = useCallback(() => {
    inspectionByIdRefetch()
      .then((res: any) => {
        if (res?.data?.success) setInspection(res?.data?.data || {});
        else
          enqueueSnackbar(
            <AlertMessages
              text={res?.data?.message || "Something went wrong"}
            />,
            { variant: "error" },
          );
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    // Using react-query refetch pattern (same style as other pages) reduces "cancelled" requests in dev StrictMode.
    getTemplateById();
    getInspectionById();
  }, []);

  useEffect(() => {
    const stationImgUrl = inspection?.attributes?.stationImgUrl
      ? inspection.attributes.stationImgUrl.split("?")[0].split("/").pop() ||
        inspection.attributes.stationImgUrl
      : undefined;
    let isActive = true;

    const getMimeTypeFromFilename = (filename: string) => {
      const clean = filename.split("?")[0].toLowerCase();
      if (clean.endsWith(".png")) return "image/png";
      if (clean.endsWith(".jpg") || clean.endsWith(".jpeg"))
        return "image/jpeg";
      if (clean.endsWith(".webp")) return "image/webp";
      if (clean.endsWith(".gif")) return "image/gif";
      if (clean.endsWith(".bmp")) return "image/bmp";
      if (clean.endsWith(".svg")) return "image/svg+xml";
      return "image/png";
    };

    const normalizeToDataUrl = (
      base64OrDataUrl: string,
      filenameForMime: string,
    ) => {
      const trimmed = (base64OrDataUrl || "").trim();
      if (!trimmed) return "";
      if (trimmed.startsWith("data:")) return trimmed;
      const mime = getMimeTypeFromFilename(filenameForMime);
      return `data:${mime};base64,${trimmed}`;
    };

    const run = async () => {
      if (!stationImgUrl) {
        setStationLogoBase64Src("");
        return;
      }
      try {
        const base64 = await CommonProfileService.convertFilenameToBase64({
          imageUrl: stationImgUrl,
        });
        if (!isActive) return;
        setStationLogoBase64Src(normalizeToDataUrl(base64, stationImgUrl));
      } catch {
        if (!isActive) return;
        setStationLogoBase64Src("");
      }
    };

    run();
    return () => {
      isActive = false;
    };
  }, [inspection?.attributes?.stationImgUrl]);

  const { getCompanyLabel, getCustomerLabel, getVehicleLabel, getTermsLabel } =
    useDefaultLabels();

  const formatDate = (date?: string) => {
    if (!date) return "-";
    const format = settings?.dateFormat || "DD-MM-YYYY";
    return dayjs(date).isValid() ? dayjs(date).format(format) : "-";
  };

  const sectionOrder = useMemo(() => {
    const order = (settings as any)?.sectionOrder;
    return Array.isArray(order) && order.length
      ? order
      : getDefaultSectionOrderForTemplateType(5);
  }, [settings]);
  const renderLanguage = (primary: any, secondary: any) => {
    const p = (primary ?? "").toString();
    const s = (secondary ?? "").toString();
    if (settings.languageDisplay === "primary") return p;
    if (settings.languageDisplay === "secondary") return s;
    return (
      <>
        <span>{p}</span>
        {s ? (
          <>
            <br />
            <span className="ff-arabic">{s}</span>
          </>
        ) : null}
      </>
    );
  };

  const coerceLabelText = (v: any): string => {
    if (v == null) return "";
    if (typeof v === "string") return v;
    if (typeof v === "number" || typeof v === "boolean") return String(v);
    if (typeof v === "object") {
      // Some templates store label values as objects (e.g. { label, value })
      if (typeof (v as any).label === "string") return (v as any).label;
      if (typeof (v as any).value === "string") return (v as any).value;
      if (
        typeof (v as any).value === "number" ||
        typeof (v as any).value === "boolean"
      )
        return String((v as any).value);
    }
    return "";
  };

  const getInspectionStatusLabel = (status?: number | null) => {
    if (status === 1) return t("working");
    if (status === 2) return t("needRepair");
    if (status === 3) return t("brokenOrMissing");
    return status == null ? t("N/A") : String(status);
  };

  const getInspectionStatusColor = (status?: number | null) => {
    if (status === 1) return "#16a34a";
    if (status === 2) return "#f59e0b";
    if (status === 3) return "#dc2626";
    return "#374151";
  };

  const renderLogoSection = () => {
    const logoUrl =
      stationLogoBase64Src ||
      inspection?.attributes?.stationImgUrl ||
      "/images/logo.png";
    const headingPrimary = settings.invoiceHeadingPrimary || "INSPECTION";
    const headingSecondary = settings.invoiceHeadingSecondary || "";
    const date = formatDate(inspection?.createdAt);
    const inspectionTitle = inspection?.inspectionFormTitle || "-";

    const logoPosition = (settings.logoPosition || "left") as string;
    const inspectionInfoPosition = (settings.invoiceNumberPosition ||
      "center") as string;
    const qrPosition = (settings.qrPosition || "right") as string;

    const showLogo = settings.showLogo !== false && logoPosition !== "none";
    const showInspectionInfo =
      settings.showInvoiceNumber !== false && inspectionInfoPosition !== "none";
    const qrCode = (inspection as any)?.qr;
    const showQR = settings.showQR !== false && qrPosition !== "none" && qrCode;

    if (!showLogo && !showInspectionInfo && !showQR) return null;

    const leftElements: JSX.Element[] = [];
    const centerElements: JSX.Element[] = [];
    const rightElements: JSX.Element[] = [];

    if (showLogo && logoPosition !== "none") {
      const logoElement = (
        <div
          key="logo"
          style={{
            display: "flex",
            justifyContent: "flex-start",
            alignItems: "center",
          }}
        >
          <img
            src={logoUrl}
            alt="Logo"
            style={{
              width:
                settings.useDefaultLogoSize === false &&
                settings.logoWidth &&
                settings.logoHeight
                  ? settings.logoWidth
                  : 150,
              maxWidth: "100%",
            }}
          />
        </div>
      );

      if (logoPosition === "left") leftElements.push(logoElement);
      else if (logoPosition === "center") centerElements.push(logoElement);
      else if (logoPosition === "right") rightElements.push(logoElement);
    }

    if (
      showInspectionInfo &&
      inspectionInfoPosition !== "none" &&
      settings.showInvoiceDirection !== "row"
    ) {
      const inspectionInfoElement = (
        <div
          key="inspection-info"
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            flexDirection: "column",
          }}
        >
          <Typography
            style={{
              fontSize: settings.invoiceNumberFontSize || 14,
              fontWeight: settings.invoiceNumberFontWeight || 700,
              color: themeColors?.secondary,
              textAlign: "center",
            }}
          >
            {renderLanguage(headingPrimary, headingSecondary)}
          </Typography>
          <Typography className="text-[13px] text-center w-[100%]">
            {t("date")}: {date}
          </Typography>
          <Typography className="text-[13px] text-center w-[100%]">
            {t("title")}: {inspectionTitle}
          </Typography>
        </div>
      );

      if (inspectionInfoPosition === "left")
        leftElements.push(inspectionInfoElement);
      else if (inspectionInfoPosition === "center")
        centerElements.push(inspectionInfoElement);
      else if (inspectionInfoPosition === "right")
        rightElements.push(inspectionInfoElement);
    }

    if (
      showQR &&
      qrPosition !== "none" &&
      settings.showInvoiceDirection !== "row"
    ) {
      const qrElement = (
        <div
          key="qr"
          style={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            marginBottom: 16,
          }}
        >
          <div
            style={{
              width: 100,
              height: 100,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <img
              src={`data:image/png;base64,${qrCode}`}
              alt="QR Code"
              style={{
                width: 100,
                height: 100,
                maxWidth: "100%",
                maxHeight: "100%",
              }}
            />
          </div>
        </div>
      );

      if (qrPosition === "left") leftElements.push(qrElement);
      else if (qrPosition === "center") centerElements.push(qrElement);
      else if (qrPosition === "right") rightElements.push(qrElement);
    }

    return (
      <div
        className="invoice-section"
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 24,
          gap: 16,
        }}
      >
        <div
          style={{
            flex: 1,
            minWidth: 0,
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
          }}
        >
          {leftElements}
        </div>

        <div
          style={{
            flex: 1,
            minWidth: 0,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          {centerElements}
        </div>

        <div
          style={{
            flex: 1,
            minWidth: 0,
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-end",
          }}
        >
          {rightElements}
        </div>
      </div>
    );
  };

  const renderInspectionInfoRow = () => {
    const showInspectionInRow = settings.showInvoiceDirection === "row";
    const showInspectionInfo = settings.showInvoiceNumber !== false;
    const infoPosition = settings.invoiceNumberPosition || "left";

    if (!showInspectionInRow || !showInspectionInfo) return null;

    const headingPrimary = settings.invoiceHeadingPrimary || "INSPECTION";
    const headingSecondary = settings.invoiceHeadingSecondary || "";
    const date = formatDate(inspection?.createdAt);
    const inspectionTitle = inspection?.inspectionFormTitle || "-";
    const qrCode = (inspection as any)?.qr;
    const showQR = settings.showQR === true && qrCode;

    return (
      <div
        className="invoice-number-row"
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 24,
          padding: "12px 16px",
          borderRadius: 8,
        }}
      >
        {infoPosition === "center" || infoPosition === "right" ? (
          <div style={{ flex: 1 }} />
        ) : null}

        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 4,
            alignItems:
              infoPosition === "left"
                ? "flex-start"
                : infoPosition === "center"
                  ? "center"
                  : "flex-end",
            flex: infoPosition === "center" ? 1 : "none",
          }}
        >
          <Typography
            style={{
              fontSize: settings.invoiceNumberFontSize || 18,
              fontWeight: settings.invoiceNumberFontWeight || 700,
              color: themeColors?.secondary,
            }}
          >
            {renderLanguage(headingPrimary, headingSecondary)}
          </Typography>
          <Typography style={{ fontSize: 13 }}>
            {t("date")}: {date}
          </Typography>
          <Typography style={{ fontSize: 13 }}>
            {t("title")}: {inspectionTitle}
          </Typography>
        </div>

        {infoPosition === "left" || infoPosition === "center" ? (
          <div style={{ flex: 1 }} />
        ) : null}

        {showQR ? (
          <div
            style={{
              width: 100,
              height: 100,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <img
              src={`data:image/png;base64,${qrCode}`}
              alt="QR Code"
              style={{
                width: 100,
                height: 100,
                maxWidth: "100%",
                maxHeight: "100%",
              }}
            />
          </div>
        ) : null}
      </div>
    );
  };

  const getTechnicianNames = (): string[] => {
    const raw = inspection?.technicians;
    if (!Array.isArray(raw)) return [];
    return raw
      .map((x: any) => {
        if (typeof x === "string") return x;
        return x?.name || x?.technicianName || x?.title || "";
      })
      .filter(Boolean) as string[];
  };

  const renderCustomerSignatureTechniciansRow = (key: string) => {
    const showSignature = settings.showCustomerSignature !== false;
    const showTechnicians = settings.showTechnicians !== false;
    if (!showSignature && !showTechnicians) return null;

    const sig = inspection?.signature || "";
    const techs = getTechnicianNames();
    if (!showSignature && !techs.length) return null;
    if (!showTechnicians && !sig) return null;

    const signatureLabel = settings.customerSignatureLabel || {};
    const techniciansLabel = settings.inspectionTechniciansLabel || {};

    return (
      <Box key={key} sx={{ mb: 2 }}>
        <Box
          sx={{
            display: "flex",
            gap: 3,
            alignItems: "flex-start",
            flexWrap: "wrap",
          }}
        >
          {showSignature ? (
            <Box sx={{ flex: "1 1 320px" }}>
              <Typography
                sx={{
                  fontWeight: 800,
                  mb: 1,
                  color: themeColors?.primary,
                  textAlign: "left",
                }}
              >
                {renderLanguage(
                  coerceLabelText(signatureLabel?.primary) ||
                    t("customerSignature"),
                  "",
                )}
              </Typography>
              <Box
                sx={{
                  border: "1px solid #e5e7eb",
                  borderRadius: 2,
                  p: 1.5,
                  width: "fit-content",
                }}
              >
                {sig ? (
                  <img src={sig} alt="signature" />
                ) : (
                  <Typography sx={{ fontSize: 11, color: "#9ca3af" }}>
                    -
                  </Typography>
                )}
              </Box>
            </Box>
          ) : null}

          {showTechnicians ? (
            <Box sx={{ flex: "1 1 320px" }}>
              <Typography
                sx={{
                  fontWeight: 800,
                  mb: 1,
                  color: themeColors?.primary,
                  textAlign: "right",
                }}
              >
                {renderLanguage(
                  coerceLabelText(techniciansLabel?.primary) ||
                    t("Technicians"),
                  "",
                )}
              </Typography>
              <Box>
                {techs.length ? (
                  techs.map((name, idx) => (
                    <Typography
                      key={idx}
                      sx={{
                        fontSize: 11,
                        fontWeight: 600,
                        color: "#111827",
                        textAlign: "right",
                      }}
                    >
                      {idx + 1}. {name}
                    </Typography>
                  ))
                ) : (
                  <Typography sx={{ fontSize: 11, color: "#9ca3af" }}>
                    -
                  </Typography>
                )}
              </Box>
            </Box>
          ) : null}
        </Box>
      </Box>
    );
  };

  const renderSection = (sectionId: string) => {
    switch (sectionId) {
      case "logo": {
        return (
          <>
            {renderLogoSection()}
            {renderInspectionInfoRow()}
          </>
        );
      }

      case "header": {
        if (settings.showHeader === false) return null;

        const companyEnabled =
          settings.companyFields || (defaultSettings.companyFields as any);
        const customerEnabled =
          settings.customerFields || (defaultSettings.customerFields as any);

        const companyFieldKeys = Object.entries(companyEnabled || {})
          .filter(([, v]) => v)
          .map(([k]) => k);
        const customerFieldKeys = Object.entries(customerEnabled || {})
          .filter(([, v]) => v)
          .map(([k]) => k);
        const maxRows = Math.max(
          companyFieldKeys.length,
          customerFieldKeys.length,
        );

        const getCompanyValue = (key: string) => {
          const station = inspection?.attributes || {};
          switch (key) {
            case "name":
              return renderLanguage(station.stationName || "", "");
            case "phone":
              return station.stationPhone || station.stationPhoneNo2 || "";
            case "email":
              return station.stationEmail || "";
            case "address":
              return station.stationAddress || "";
            case "vat":
              return station.stationTaxNumber || "";
            default:
              return "";
          }
        };

        const getCustomerValue = (key: string) => {
          switch (key) {
            case "name":
              return renderLanguage(
                inspection?.attributes?.companyName ||
                  inspection?.attributes?.customerName ||
                  "",
                "",
              );
            case "phone":
              return (
                inspection?.attributes?.companyPhone ||
                inspection?.attributes?.customerPhone ||
                ""
              );
            case "email":
              return (
                inspection?.attributes?.companyEmail ||
                inspection?.attributes?.customerEmail ||
                ""
              );
            case "address":
              return (
                inspection?.attributes?.companyAddress ||
                inspection?.attributes?.customerAddress ||
                ""
              );
            case "vat":
              return (
                inspection?.attributes?.companyTaxNumber ||
                inspection?.attributes?.customerTaxNumber ||
                ""
              );
            default:
              return "";
          }
        };

        const getCompanyLabelText = (key: string) => {
          const label = (settings.companyLabels || {}) as any;
          return renderLanguage(
            coerceLabelText(label?.[key]?.primary) ||
              getCompanyLabel(key).primary ||
              t(key),
            "",
          );
        };

        const getCustomerLabelText = (key: string) => {
          const label = (settings.customerLabels || {}) as any;
          return renderLanguage(
            coerceLabelText(label?.[key]?.primary) ||
              getCustomerLabel(key).primary ||
              t(key),
            "",
          );
        };

        const languageDisplay = settings.languageDisplay || "both";
        const showCompany = settings.showCompanySection !== false;
        const showCustomer = settings.showCustomerSection !== false;

        const rows: JSX.Element[] = [];
        for (let i = 0; i < maxRows; i++) {
          const companyKey = companyFieldKeys[i];
          const customerKey = customerFieldKeys[i];

          rows.push(
            <tr key={i}>
              {showCompany ? (
                <td
                  colSpan={2}
                  style={{
                    border: "1px solid #ddd",
                    padding: "8px",
                    width: showCustomer ? "50%" : "100%",
                  }}
                >
                  {companyKey ? (
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      {languageDisplay !== "secondary" ? (
                        <span
                          style={{
                            fontWeight: 500,
                            textAlign: "left",
                            width: "30%",
                          }}
                        >
                          {getCompanyLabelText(companyKey)}:
                        </span>
                      ) : null}
                      <span style={{ width: "40%", wordBreak: "break-word" }}>
                        {getCompanyValue(companyKey)}
                      </span>
                      {languageDisplay !== "primary" ? (
                        <span
                          style={{
                            fontWeight: 500,
                            textAlign: "right",
                            width: "30%",
                          }}
                          className={
                            secondaryLanguage === "arabic" ||
                            secondaryLanguage === "urdu"
                              ? "ff-arabic"
                              : ""
                          }
                        >
                          :
                          {coerceLabelText(
                            (settings.companyLabels as any)?.[companyKey]
                              ?.secondary,
                          ) ||
                            getCompanyLabel(companyKey).secondary ||
                            ""}
                        </span>
                      ) : null}
                    </div>
                  ) : null}
                </td>
              ) : null}

              {showCustomer ? (
                <td
                  colSpan={2}
                  style={{
                    border: "1px solid #ddd",
                    padding: "8px",
                    width: showCompany ? "50%" : "100%",
                  }}
                >
                  {customerKey ? (
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      {languageDisplay !== "secondary" ? (
                        <span
                          style={{
                            fontWeight: 500,
                            textAlign: "left",
                            width: "30%",
                          }}
                        >
                          {getCustomerLabelText(customerKey)}:
                        </span>
                      ) : null}
                      <span style={{ width: "40%", wordBreak: "break-word" }}>
                        {getCustomerValue(customerKey)}
                      </span>
                      {languageDisplay !== "primary" ? (
                        <span
                          style={{
                            fontWeight: 500,
                            textAlign: "right",
                            width: "30%",
                          }}
                          className={
                            secondaryLanguage === "arabic" ||
                            secondaryLanguage === "urdu"
                              ? "ff-arabic"
                              : ""
                          }
                        >
                          :
                          {coerceLabelText(
                            (settings.customerLabels as any)?.[customerKey]
                              ?.secondary,
                          ) ||
                            getCustomerLabel(customerKey).secondary ||
                            ""}
                        </span>
                      ) : null}
                    </div>
                  ) : null}
                </td>
              ) : null}
            </tr>,
          );
        }

        return (
          <Box key="header" sx={{ mb: 3 }}>
            {rows.length && (showCompany || showCustomer) ? (
              <div style={{ marginBottom: 16 }}>
                <table
                  style={{
                    width: "100%",
                    borderCollapse: "collapse",
                    border: "1px solid #ddd",
                  }}
                >
                  <tbody style={{ fontSize: 13 }}>{rows}</tbody>
                </table>
              </div>
            ) : null}
          </Box>
        );
      }

      case "vehicleInfo": {
        if (settings.showVehicleInfo === false) return null;
        const label = settings.vehicleLabels || {};
        const v = inspection?.attributes || {};
        const enabled =
          settings.vehicleFields || (defaultSettings.vehicleFields as any);

        const fields = [
          { key: "plateNumber", value: v.plateNumber || "" },
          { key: "vin", value: v.vehicleVin || "" },
          { key: "make", value: v.makeTitle || "" },
          { key: "model", value: v.modelTitle || "" },
          { key: "year", value: v.year || "" },
          { key: "color", value: v.color || "" },
          { key: "fuelType", value: v.fuelTypeTitle || "" },
          { key: "mileage", value: (v.odometer ?? "").toString() },
        ].filter((f) => (enabled as any)?.[f.key] && f.value);

        if (!fields.length) return null;

        const vehicleTitle =
          `${v.makeTitle || ""}${v.makeTitle && v.modelTitle ? "-" : ""}${v.modelTitle || ""}`.trim();
        const plateNo = v.plateNumber || "";

        const languageDisplay = settings.languageDisplay || "both";
        const rows: JSX.Element[] = [];
        for (let i = 0; i < fields.length; i += 2) {
          const f1 = fields[i];
          const f2 = fields[i + 1];
          rows.push(
            <tr key={f1.key}>
              <td
                style={{
                  border: "1px solid #ddd",
                  padding: "8px",
                  width: "50%",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  {languageDisplay !== "secondary" ? (
                    <span style={{ fontWeight: 500 }}>
                      {coerceLabelText((label as any)?.[f1.key]?.primary) ||
                        getVehicleLabel(f1.key).primary ||
                        t(f1.key)}
                      :
                    </span>
                  ) : null}
                  <span
                    style={{
                      flex: "1 1 auto",
                      paddingLeft: 8,
                      paddingRight: 8,
                      wordBreak: "break-word",
                    }}
                  >
                    {f1.value}
                  </span>
                  {languageDisplay !== "primary" ? (
                    <span
                      style={{ fontWeight: 500 }}
                      className={
                        secondaryLanguage === "arabic" ||
                        secondaryLanguage === "urdu"
                          ? "ff-arabic"
                          : ""
                      }
                    >
                      :
                      {coerceLabelText((label as any)?.[f1.key]?.secondary) ||
                        getVehicleLabel(f1.key).secondary ||
                        ""}
                    </span>
                  ) : null}
                </div>
              </td>
              {f2 ? (
                <td
                  style={{
                    border: "1px solid #ddd",
                    padding: "8px",
                    width: "50%",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    {languageDisplay !== "secondary" ? (
                      <span style={{ fontWeight: 500 }}>
                        {coerceLabelText((label as any)?.[f2.key]?.primary) ||
                          getVehicleLabel(f2.key).primary ||
                          t(f2.key)}
                        :
                      </span>
                    ) : null}
                    <span
                      style={{
                        flex: "1 1 auto",
                        paddingLeft: 8,
                        paddingRight: 8,
                        wordBreak: "break-word",
                      }}
                    >
                      {f2.value}
                    </span>
                    {languageDisplay !== "primary" ? (
                      <span
                        style={{ fontWeight: 500 }}
                        className={
                          secondaryLanguage === "arabic" ||
                          secondaryLanguage === "urdu"
                            ? "ff-arabic"
                            : ""
                        }
                      >
                        :
                        {coerceLabelText((label as any)?.[f2.key]?.secondary) ||
                          getVehicleLabel(f2.key).secondary ||
                          ""}
                      </span>
                    ) : null}
                  </div>
                </td>
              ) : (
                <td style={{ border: "1px solid #ddd", width: "50%" }} />
              )}
            </tr>,
          );
        }

        return (
          <Box key="vehicleInfo" sx={{ mb: 3 }}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                marginBottom: 8,
                alignItems: "center",
              }}
            >
              <Typography
                style={{
                  color: themeColors?.primary,
                  margin: 0,
                  fontSize: "13px",
                  fontWeight: "700",
                }}
              >
                {vehicleTitle || t("vehicleInfo")}
              </Typography>
              <Typography
                style={{
                  color: themeColors?.primary,
                  margin: 0,
                  fontSize: "13px",
                  fontWeight: "700",
                }}
              >
                {t("vehiclePlateNo")}: {plateNo || t("N/A")}
              </Typography>
            </div>

            <div style={{ marginBottom: 16 }}>
              <table
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  border: "1px solid #ddd",
                }}
              >
                <tbody style={{ fontSize: 13 }}>{rows}</tbody>
              </table>
            </div>
          </Box>
        );
      }

      case "inspectionItems": {
        if (settings.showInspectionItems === false) return null;
        const rawSections = inspection?.sections || [];
        const sections = rawSections
          .map((sec) => ({
            ...sec,
            items: (sec?.items || []).filter(
              (it: any) =>
                it?.status === 1 || it?.status === 2 || it?.status === 3,
            ),
          }))
          .filter((sec) => (sec?.items || []).length);
        if (!sections.length) return null;

        return (
          <Box key="inspectionItems" sx={{ mb: 2 }}>
            <Box
              sx={{
                display: "flex",
                gap: 2,
                alignItems: "flex-start",
                flexWrap: "wrap",
              }}
            >
              <Box sx={{ flex: "1 1 520px" }}>
                {sections.map((sec, secIdx) => {
                  const items = sec?.items || [];
                  if (!items.length) return null;
                  return (
                    <Box key={sec?.sectionId || secIdx} sx={{ mb: 2 }}>
                      <Typography
                        sx={{
                          fontWeight: 800,
                          mb: 1,
                          color: themeColors?.primary,
                          textAlign: "left",
                        }}
                      >
                        {renderLanguage(
                          sec?.title || `${t("section")} ${secIdx + 1}`,
                          "",
                        )}
                      </Typography>

                      <TableContainer
                        sx={{ border: "1px solid #e5e7eb", borderRadius: 2 }}
                      >
                        <Table size="small">
                          <TableHead>
                            <TableRow sx={{ background: "#f9fafb" }}>
                              <TableCell sx={{ fontWeight: 800, width: 50 }}>
                                #
                              </TableCell>
                              <TableCell sx={{ fontWeight: 800 }}>
                                {t("partName")}
                              </TableCell>
                              <TableCell sx={{ fontWeight: 800, width: 130 }}>
                                {t("status")}
                              </TableCell>
                              <TableCell sx={{ fontWeight: 800 }}>
                                {t("notes")}
                              </TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {items.map((it, idx) => (
                              <TableRow
                                key={`${it?.inspectionFormItemId || idx}`}
                              >
                                <TableCell>{idx + 1}</TableCell>
                                <TableCell>
                                  <Typography
                                    sx={{ fontWeight: 700, fontSize: 11 }}
                                  >
                                    {renderLanguage(
                                      it?.title || "",
                                      it?.secondaryTitle || "",
                                    )}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Typography
                                    sx={{
                                      fontWeight: 800,
                                      fontSize: 11,
                                      color: getInspectionStatusColor(
                                        it?.status,
                                      ),
                                    }}
                                  >
                                    {getInspectionStatusLabel(it?.status)}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Typography
                                    sx={{
                                      fontSize: 10,
                                      color: "#111827",
                                      whiteSpace: "pre-line",
                                    }}
                                  >
                                    {it?.notes || "-"}
                                  </Typography>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>

                      {/* Item attachments (images) */}
                      {items.some((it) => (it?.imageUrls || []).length) ? (
                        <Box
                          sx={{
                            mt: 1.5,
                            display: "flex",
                            gap: 1,
                            flexWrap: "wrap",
                          }}
                        >
                          {items
                            .flatMap((it) =>
                              (it?.imageUrls || []).map((url, i) => ({
                                url,
                                key: `${it?.inspectionFormItemId || "it"}-${i}`,
                              })),
                            )
                            .map((img) => (
                              <Box
                                key={img.key}
                                sx={{
                                  width: 140,
                                  height: 100,
                                  borderRadius: 2,
                                  overflow: "hidden",
                                  border: "1px solid #e5e7eb",
                                  background: "#fff",
                                }}
                              >
                                <img
                                  src={img.url}
                                  alt="inspection"
                                  style={{
                                    width: "100%",
                                    height: "100%",
                                    objectFit: "cover",
                                  }}
                                />
                              </Box>
                            ))}
                        </Box>
                      ) : null}
                    </Box>
                  );
                })}
              </Box>

              {settings.showVehicleImage !== false ? (
                <Box
                  sx={{
                    flex: "1 1 360px",
                    border: "1px solid #e5e7eb",
                    borderRadius: 2,
                    p: 1.5,
                    marginTop: "15px",
                  }}
                >
                  <Box
                    sx={{
                      position: "relative",
                      width: "100%",
                      aspectRatio: "4 / 3",
                      borderRadius: 2,
                      overflow: "hidden",
                      background: "#fff",
                    }}
                  >
                    <InspectionVehicleImg
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "contain",
                      }}
                    />
                    {(inspection?.markings || []).map((m, mi) => {
                      const cw = m?._canvasWidth || 1;
                      const ch = m?._canvasHeight || 1;
                      const x = (m?.x || 0) / cw;
                      const y = (m?.y || 0) / ch;
                      const left = Math.max(0, Math.min(1, x)) * 100;
                      const top = Math.max(0, Math.min(1, y)) * 100;
                      return (
                        <Box
                          key={m?.id || mi}
                          sx={{
                            position: "absolute",
                            left: `${left}%`,
                            top: `${top}%`,
                            transform: "translate(-50%, -50%)",
                            width: 22,
                            height: 22,
                            borderRadius: "999px",
                            background: m?.color || "#ef4444",
                            color: "#fff",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: 11,
                            fontWeight: 800,
                            border: "2px solid #fff",
                            boxShadow: "0 2px 10px rgba(0,0,0,0.15)",
                          }}
                          title={m?.description || ""}
                        >
                          {m?.number || mi + 1}
                        </Box>
                      );
                    })}
                  </Box>
                </Box>
              ) : null}
            </Box>
          </Box>
        );
      }

      case "inspectionRemarks": {
        if (settings.showInspectionRemarks === false) return null;
        const remark = inspection?.remark || "";
        if (!remark) return null;
        return (
          <Box key="inspectionRemarks" sx={{ mb: 2 }}>
            <Typography
              sx={{
                fontWeight: 800,
                mb: 1,
                color: themeColors?.primary,
                textAlign: "left",
              }}
            >
              {renderLanguage(
                settings.inspectionRemarksLabel?.primary ||
                  t("inspectionRemarks"),
                "",
              )}
            </Typography>
            <Box
              sx={{
                border: "1px solid #e5e7eb",
                borderRadius: 2,
                p: 1.5,
                textAlign: "left",
                fontSize: 11,
                color: "#111827",
              }}
            >
              {remark}
            </Box>
          </Box>
        );
      }

      case "technicians": {
        if (
          sectionOrder.some(
            (id) => id === "customerSignature" || id === "signature",
          )
        )
          return null;
        return renderCustomerSignatureTechniciansRow("technicians");
      }

      case "terms": {
        if (settings.showTerms === false) return null;
        const primary =
          inspection?.termAndCondition ||
          inspection?.attributes?.stationTermAndCondition ||
          "";
        const secondary =
          inspection?.secondaryTermAndCondition ||
          inspection?.attributes?.stationTermAndConditionSecondary ||
          "";
        if (!primary && !secondary) return null;
        const labelPrimary = ((settings as any).termsLabel?.primary ||
          getTermsLabel().primary ||
          t("termsAndConditions")) as string;
        const labelSecondary = ((settings as any).termsLabel?.secondary ||
          getTermsLabel().secondary ||
          "") as string;
        const languageDisplay = settings.languageDisplay || "both";
        const isSecondaryArabicOrUrdu =
          secondaryLanguage === "arabic" || secondaryLanguage === "urdu";

        return (
          <Box key="terms" sx={{ mb: 2 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                gap: 2,
              }}
            >
              {languageDisplay !== "secondary" ? (
                <Box sx={{ flex: 1 }}>
                  <Typography
                    sx={{
                      fontWeight: 800,
                      mb: 1,
                      color: themeColors?.primary,
                      textAlign: "left",
                    }}
                  >
                    {labelPrimary}
                  </Typography>
                  <Typography
                    sx={{ fontSize: 11, color: "#111827", textAlign: "left" }}
                  >
                    {primary || "-"}
                  </Typography>
                </Box>
              ) : null}
              {languageDisplay !== "primary" && secondary ? (
                <Box
                  sx={{
                    flex: 1,
                    textAlign:
                      languageDisplay === "secondary" ? "left" : "right",
                  }}
                >
                  <Typography
                    sx={{ fontWeight: 800, mb: 1, color: themeColors?.primary }}
                    className={isSecondaryArabicOrUrdu ? "ff-arabic" : ""}
                  >
                    {labelSecondary}
                  </Typography>
                  <Typography
                    sx={{ fontSize: 11, color: "#111827" }}
                    className={isSecondaryArabicOrUrdu ? "ff-arabic" : ""}
                  >
                    {secondary}
                  </Typography>
                </Box>
              ) : null}
            </Box>
          </Box>
        );
      }

      case "customerSignature":
      case "signature": {
        return renderCustomerSignatureTechniciansRow("customerSignature");
      }

      case "footer": {
        if (settings.showFooter === false) return null;

        const footerItems =
          settings?.footerItems || defaultSettings.footerItems || {};
        const leftItems: string[] = [];
        const station = inspection?.attributes || {};

        if (footerItems.address && station.stationAddress) {
          leftItems.push(station.stationAddress);
        }
        if (footerItems.email && station.stationEmail) {
          leftItems.push(station.stationEmail);
        }
        if (
          footerItems.phone &&
          (station.stationPhone || station.stationPhoneNo2)
        ) {
          leftItems.push(station.stationPhone || station.stationPhoneNo2 || "");
        }

        const footerText = settings.footerText || "";
        const brandingFooterText =
          branding?.attributes?.footerText?.trim() || "";
        const brandingAboutHref = getBrandingAboutHref(
          branding?.attributes?.about,
        );
        const hasFooterContent = Boolean(
          footerText || leftItems.length || brandingFooterText,
        );

        if (!hasFooterContent) return null;

        return (
          <Box key="footer" sx={{ mt: 3, pt: 2, borderTop: "1px solid #ddd" }}>
            {footerText ? (
              <Typography
                variant="caption"
                align="center"
                display="block"
                sx={{
                  mb: leftItems.length || brandingFooterText ? 2 : 0,
                }}
              >
                {footerText}
              </Typography>
            ) : null}
            {leftItems.length ? (
              <Box className="flex gap-2 justify-between">
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 1.5,
                    alignItems: "center",
                    flex: 1,
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{ fontSize: "0.75rem", color: "#666" }}
                  >
                    {leftItems.join(" | ")}
                  </Typography>
                </Box>
              </Box>
            ) : null}
            {brandingFooterText ? (
              <Typography
                variant="caption"
                align="center"
                display="block"
                className="mt-[15px]"
              >
                {brandingAboutHref ? (
                  <a
                    href={brandingAboutHref}
                    target="_blank"
                    rel="noreferrer"
                    style={{ color: themeColors?.primary }}
                  >
                    {brandingFooterText}
                  </a>
                ) : (
                  brandingFooterText
                )}
              </Typography>
            ) : null}
          </Box>
        );
      }

      default:
        return null;
    }
  };

  const thermalWidthMm = isThermalPrint(settings)
    ? getThermalPaperWidthMm(settings)
    : null;

  return (
    <>
      {isRefetching || inspectionRefetching || loading ? (
        <Loader />
      ) : (
        <>
          <Box
            className="flex justify-between items-center hide-on-print bg-[#fff] rounded-[8px] mb-[15px]"
            sx={{
              p: 2,
              maxWidth: thermalWidthMm ? `${thermalWidthMm}mm` : "1000px",
              margin: "0 auto",
            }}
            id="form"
          >
            <Button
              className="secondary"
              variant="contained"
              style={{ color: themeColors?.primary }}
              disableElevation
              onClick={() => navigate(-1)}
            >
              {t("back")}
            </Button>
            <Box className="flex gap-2">
              <Button
                className="primary"
                variant="contained"
                style={{ background: themeColors?.primary }}
                disableElevation
                onClick={() => window.print()}
              >
                {t("Print")}
              </Button>
              <Button
                className="primary"
                variant="contained"
                style={{
                  display: "none",
                  background: "transparent",
                  color: themeColors?.secondary,
                  border: "2px solid" + themeColors?.secondary,
                }}
                disableElevation
                onClick={() => setDownloading(true)}
              >
                {downloading ? (
                  <CircularProgress
                    sx={{ color: themeColors?.primary, marginRight: "8px" }}
                    size={16}
                  />
                ) : (
                  t("download")
                )}
              </Button>
            </Box>
          </Box>

          <Box
            id="print-root"
            ref={printRef}
            sx={{
              p: 2,
              maxWidth: thermalWidthMm ? `${thermalWidthMm}mm` : "1000px",
              margin: "0 auto",
              backgroundColor: "#fff",
              borderRadius: "8px",
              fontFamily: "Saira, sans-serif",
              "@media print": thermalWidthMm
                ? {
                    maxWidth: `${thermalWidthMm}mm`,
                    width: `${thermalWidthMm}mm`,
                    margin: 0,
                    p: 0,
                  }
                : {},
            }}
          >
            <Box>
              {sectionOrder.map((sectionId: any) => renderSection(sectionId))}
            </Box>
          </Box>
        </>
      )}
    </>
  );
};

export default InspectionDownloadPreview;
